// _productsNavSp.ts
interface NavItem {
  title: string;
  href?: string;
  children?: NavItem[];
  classes?: string[];
}

export class SingleProductsNavSp {
  private rootElement: HTMLElement;
  private navData: NavItem[] = [];
  private maxDepth: number = 1;
  private currentPath: number[] = [];
  private wrapper: HTMLElement;
  private levelContainers: HTMLElement[] = [];
  private mutationObserver: MutationObserver;

  private focusableElements: HTMLElement[] = [];
  private firstFocusableElement?: HTMLElement;
  private lastFocusableElement?: HTMLElement;
  private titleLink?: HTMLAnchorElement | null;
  private isActive: boolean = false; // ナビが有効（開いている）か

  // 各階層移動のトリガーとなったリンクを記録する配列
  private triggerElements: HTMLElement[] = [];

  constructor(rootElement: HTMLElement) {
    this.rootElement = rootElement;
    this.wrapper = document.createElement('div');
    this.mutationObserver = new MutationObserver(() => {});

    const navContent = this.rootElement.querySelector(
      '.ist-productsTop__navContent'
    ) as HTMLElement;
    const originalUl = navContent?.querySelector(
      'ul.ist-productsTop__navList'
    ) as HTMLUListElement;
    if (!originalUl) return;

    this.navData = this.parseUl(originalUl);
    this.maxDepth = this.getMaxDepth(this.navData);

    this.wrapper.classList.add('ist-productsTop__navListWrapper');
    navContent.insertAdjacentElement('afterbegin', this.wrapper);

    // 各階層コンテナに固有のIDを付与し、aria-hidden を設定
    for (let level = 1; level <= this.maxDepth; level++) {
      const levelDiv = document.createElement('div');
      levelDiv.classList.add(
        'ist-productsTop__navListContainer',
        `ist-productsTop__navListContainer--level${level}`
      );
      levelDiv.id = `ist-productsTop__navListContainer-level${level}`;
      if (level === 1) {
        levelDiv.classList.add('ist-productsTop__navListContainer--isActive');
      } else {
        levelDiv.setAttribute('aria-hidden', 'true');
      }
      this.levelContainers.push(levelDiv);
      this.wrapper.appendChild(levelDiv);
    }

    this.renderLevelItems(this.navData, 1);
    originalUl.style.display = 'none';

    this.titleLink = this.rootElement.querySelector(
      '.ist-productsTop__navTitle'
    ) as HTMLAnchorElement | null;

    this.attachEvents();
    this.observeOpenState();
    this.updateFocusableElements();
    this.focusFirstElement();

    // フォーカスアウトイベントを設定
    this.attachFocusOutEvent();
  }

  private attachFocusOutEvent() {
    this.rootElement.addEventListener('focusout', (e) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (!relatedTarget || !this.rootElement.contains(relatedTarget)) {
        // フォーカスがナビ領域外へ移動した場合、ナビをリセット・非アクティブ化
        this.resetNavigation();
        this.deactivate();
      }
    });
  }

  private parseUl(ul: HTMLUListElement): NavItem[] {
    const items: NavItem[] = [];
    ul.querySelectorAll(':scope > li.ist-productsTop__navItem').forEach(
      (li) => {
        const link = li.querySelector('a.ist-productsTop__navLink');
        if (!link) return;
        if (link.classList.contains('ist-productsTop__navLink--back')) return;

        const title = link.textContent?.trim() || '';
        const href = link.getAttribute('href') || '#';
        const classes = Array.from(link.classList);
        const childUl = li.querySelector(
          ':scope > ul.ist-productsTop__navList'
        );

        let children: NavItem[] | undefined;
        if (childUl instanceof HTMLUListElement) {
          children = this.parseUl(childUl);
        }

        items.push({
          title,
          href: href !== '#' ? href : undefined,
          children: children && children.length > 0 ? children : undefined,
          classes,
        });
      }
    );
    return items;
  }

  private getMaxDepth(items: NavItem[]): number {
    let maxDepth = 1;
    const checkDepth = (nodes: NavItem[], depth: number) => {
      if (depth > maxDepth) maxDepth = depth;
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          checkDepth(node.children, depth + 1);
        }
      });
    };
    checkDepth(items, 1);
    return maxDepth;
  }

  private getCurrentLevelData(): NavItem[] {
    let current: NavItem[] = this.navData;
    for (let i = 0; i < this.currentPath.length; i++) {
      const idx = this.currentPath[i];
      const item = current[idx];
      if (item && item.children) {
        current = item.children;
      } else {
        return [];
      }
    }
    return current;
  }

  private renderLevelItems(
    items: NavItem[],
    level: number,
    parentTitle?: string
  ) {
    const levelIndex = level - 1;
    const container = this.levelContainers[levelIndex];
    container.innerHTML = '';

    const ul = document.createElement('ul');
    ul.classList.add('ist-productsTop__navList');
    container.appendChild(ul);

    if (level > 1 && parentTitle) {
      const backLi = document.createElement('li');
      backLi.classList.add('ist-productsTop__navItem');
      const backA = document.createElement('a');
      backA.classList.add(
        'ist-productsTop__navLink',
        'ist-productsTop__navLink--back'
      );
      backA.setAttribute('href', '#');
      backA.textContent = parentTitle;
      if (
        !container.classList.contains(
          'ist-productsTop__navListContainer--isActive'
        )
      ) {
        backA.setAttribute('tabindex', '-1');
      }
      backLi.appendChild(backA);
      ul.appendChild(backLi);
    }

    items.forEach((item, i) => {
      const li = document.createElement('li');
      li.classList.add('ist-productsTop__navItem');

      const a = document.createElement('a');
      if (item.classes) {
        a.classList.add(...item.classes);
      }
      a.textContent = item.title;
      a.setAttribute('href', item.href || '#');
      (a as any).dataset.index = i.toString();

      // 子メニューがある場合、ARIA属性を設定
      if (item.children && item.children.length > 0) {
        a.setAttribute('aria-expanded', 'false');
        a.setAttribute(
          'aria-controls',
          `ist-productsTop__navListContainer-level${level + 1}`
        );
      }

      if (
        !container.classList.contains(
          'ist-productsTop__navListContainer--isActive'
        )
      ) {
        a.setAttribute('tabindex', '-1');
      }

      li.appendChild(a);
      ul.appendChild(li);
    });
  }

  private attachEvents() {
    this.rootElement.addEventListener('keydown', (e) => {
      if (!this.isActive) return;
      if (e.key !== 'Tab') return;
      if (!this.firstFocusableElement || !this.lastFocusableElement) return;

      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement.focus();
        }
      }
    });

    this.wrapper.addEventListener('click', (ev) => {
      ev.preventDefault();
      const target = ev.target as HTMLElement;

      if (!target.classList.contains('ist-productsTop__navLink')) return;

      if (target.classList.contains('ist-productsTop__navLink--back')) {
        const currentLevel = this.currentPath.length + 1;
        const previousLevel = currentLevel - 1;

        const currentContainer = this.levelContainers[currentLevel - 1];
        currentContainer.classList.remove(
          'ist-productsTop__navListContainer--isActive'
        );
        currentContainer.setAttribute('aria-hidden', 'true');
        currentContainer.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', '-1');
        });

        const previousContainer = this.levelContainers[previousLevel - 1];
        previousContainer.classList.add(
          'ist-productsTop__navListContainer--isActive'
        );
        previousContainer.removeAttribute('aria-hidden');
        previousContainer.querySelectorAll('a').forEach((link) => {
          link.removeAttribute('tabindex');
        });

        this.rootElement.classList.remove(
          `ist-productsTop__navContent--level${currentLevel}`
        );
        this.rootElement.classList.add(
          `ist-productsTop__navContent--level${previousLevel}`
        );

        // バック移動時、記録していたトリガーリンクの aria-expanded を false に更新
        const triggerElement = this.triggerElements.pop();
        if (triggerElement) {
          triggerElement.setAttribute('aria-expanded', 'false');
        }
        this.currentPath.pop();
        this.updateFocusableElements();
        if (triggerElement) {
          triggerElement.focus();
        } else {
          this.focusFirstElement();
        }
        return;
      }

      const idxStr = (target as any).dataset.index;
      if (idxStr != null) {
        const idx = parseInt(idxStr, 10);
        const currentItems = this.getCurrentLevelData();
        const item = currentItems[idx];

        if (item?.children?.length) {
          // 子メニュー移動時、トリガーリンクを記録し aria-expanded を true に更新
          this.currentPath.push(idx);
          this.triggerElements.push(target);
          target.setAttribute('aria-expanded', 'true');
          this.updateDisplay(item.title);
          this.focusFirstElement();
        } else if (item?.href && item.href !== '#') {
          window.location.href = item.href;
        }
      }
    });
  }

  private updateDisplay(parentTitle?: string) {
    const currentLevel = this.currentPath.length + 1;
    const items = this.getCurrentLevelData();

    Array.from(this.rootElement.classList)
      .filter((className) =>
        className.startsWith('ist-productsTop__navContent--level')
      )
      .forEach((className) => this.rootElement.classList.remove(className));

    this.rootElement.classList.add(
      `ist-productsTop__navContent--level${currentLevel}`
    );

    this.levelContainers.forEach((container, index) => {
      const isCurrentLevel = index === currentLevel - 1;

      container.classList.toggle(
        'ist-productsTop__navListContainer--isActive',
        isCurrentLevel
      );

      if (isCurrentLevel) {
        container.removeAttribute('aria-hidden');
        container.querySelectorAll('a').forEach((link) => {
          link.removeAttribute('tabindex');
        });
      } else {
        container.setAttribute('aria-hidden', 'true');
        container.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', '-1');
        });
      }
    });

    const currentContainer = this.levelContainers[currentLevel - 1];
    if (currentContainer) {
      this.renderLevelItems(items, currentLevel, parentTitle);
    }

    this.updateFocusableElements();
  }

  private resetNavigation() {
    this.levelContainers.forEach((container, index) => {
      if (index === 0) {
        container.classList.add('ist-productsTop__navListContainer--isActive');
        container.removeAttribute('aria-hidden');
        container.querySelectorAll('a').forEach((link) => {
          link.removeAttribute('tabindex');
        });
      } else {
        container.classList.remove(
          'ist-productsTop__navListContainer--isActive'
        );
        container.setAttribute('aria-hidden', 'true');
        container.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', '-1');
        });
      }
    });

    Array.from(this.rootElement.classList)
      .filter((className) =>
        className.startsWith('ist-productsTop__navContent--level')
      )
      .forEach((className) => this.rootElement.classList.remove(className));
    this.rootElement.classList.add('ist-productsTop__navContent--level1');

    this.renderLevelItems(this.navData, 1);
    this.currentPath = [];
    this.triggerElements = [];
    this.updateFocusableElements();
    this.focusFirstElement();
  }

  private observeOpenState() {
    const parentNavItem = this.rootElement.closest('.ist-productsTop__navItem');
    if (!parentNavItem) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const hasOpenClass = parentNavItem.classList.contains(
            'ist-productsTop__navItem--isOpen'
          );
          if (!hasOpenClass) {
            this.resetNavigation();
            this.deactivate();
          }
        }
      }
    });

    this.mutationObserver.observe(parentNavItem, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  private updateFocusableElements() {
    const activeContainer = this.levelContainers.find((container) =>
      container.classList.contains(
        'ist-productsTop__navListContainer--isActive'
      )
    );
    let focusable: HTMLElement[] = [];
    if (activeContainer) {
      focusable = Array.from(
        activeContainer.querySelectorAll('a:not([tabindex="-1"])')
      ) as HTMLElement[];
    }

    if (this.titleLink) {
      this.focusableElements = [this.titleLink, ...focusable];
    } else {
      this.focusableElements = focusable;
    }

    if (this.focusableElements.length > 0) {
      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement =
        this.focusableElements[this.focusableElements.length - 1];
    } else {
      this.firstFocusableElement = undefined;
      this.lastFocusableElement = undefined;
    }
  }

  private focusFirstElement() {
    if (this.isActive && this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
  }

  public activate() {
    this.isActive = true;
    this.focusFirstElement();
  }

  public deactivate() {
    this.isActive = false;
  }
}

export class ProductsNavSp {
  private targets: HTMLElement[];
  private navFocusManagers: SingleProductsNavSp[] = [];

  constructor() {
    this.targets = Array.from(
      document.querySelectorAll('.ist-mb .ist-productsTop__navContent')
    ) as HTMLElement[];
    this.init();
  }

  private init() {
    this.targets.forEach((navContentElement) => {
      const rootElement = navContentElement.closest(
        '.ist-productsTop__navInner'
      ) as HTMLElement;
      if (!rootElement) return;
      const navManager = new SingleProductsNavSp(rootElement);
      this.navFocusManagers.push(navManager);
    });
  }

  public getFocusManagers() {
    return this.navFocusManagers;
  }
}
